
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Copy, Eye, EyeOff, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

const AccessCredentials = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [apiKey, setApiKey] = useState("FE-" + Math.random().toString(36).substring(2, 15));
  const [showApiKey, setShowApiKey] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // In a real implementation, you would fetch the API key from your backend
  // This is just a simulation for demo purposes
  
  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    toast({
      title: "API Key Copied",
      description: "The API key has been copied to your clipboard.",
    });
  };

  const handleRegenerateApiKey = () => {
    setIsRegenerating(true);
    
    // Simulate API call
    setTimeout(() => {
      setApiKey("FE-" + Math.random().toString(36).substring(2, 15));
      setIsRegenerating(false);
      toast({
        title: "API Key Regenerated",
        description: "Your API key has been successfully regenerated.",
      });
    }, 1000);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Access Credentials</CardTitle>
        <CardDescription>
          Manage your API keys and access tokens for integrating with FraudEye services.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="api-key">API Key</Label>
          <div className="flex">
            <Input
              id="api-key"
              value={showApiKey ? apiKey : "•".repeat(24)}
              readOnly
              className="rounded-r-none font-mono"
            />
            <Button
              variant="outline"
              size="icon"
              className="rounded-l-none border-l-0"
              onClick={() => setShowApiKey(!showApiKey)}
            >
              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Your API key provides access to our services. Keep it secure and don't share it publicly.
          </p>
        </div>
        
        <div className="flex justify-between">
          <Button variant="outline" onClick={handleCopyApiKey}>
            <Copy className="mr-2 h-4 w-4" />
            Copy
          </Button>
          
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="secondary">
                <RefreshCw className="mr-2 h-4 w-4" />
                Regenerate
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Regenerate API Key?</DialogTitle>
                <DialogDescription>
                  This action will invalidate your current API key and generate a new one. Any applications using your current key will need to be updated.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => {}}>Cancel</Button>
                <Button 
                  onClick={handleRegenerateApiKey} 
                  disabled={isRegenerating}
                  className="bg-fraudeye-accent hover:bg-fraudeye-highlight"
                >
                  {isRegenerating ? "Regenerating..." : "Regenerate"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col items-start">
        <div className="text-sm text-muted-foreground">
          <p className="mb-2">Integration details:</p>
          <p>User ID: {user?.id || "Not logged in"}</p>
          <p>Endpoint: https://api.fraudeye.app/v1/</p>
        </div>
      </CardFooter>
    </Card>
  );
};

export default AccessCredentials;
